import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = ["Overview", "CRM Funnel", "SEO & Search", "Analytics", "Ads & ROAS", "GBP & Revenue", "Campaign & Ad Copy", "Connections"];
const PERIODS = [["7", "7 Days"], ["28", "28 Days"], ["90", "90 Days"], ["year", "This Year"]];
const anton = { fontFamily: "'Anton', sans-serif" };
const manrope = { fontFamily: "'Manrope', sans-serif" };

const Label = ({ children }) => (
  <p className="text-[10px] uppercase text-zinc-500 font-semibold" style={{ letterSpacing: "0.22em", ...manrope }}>{children}</p>
);

const Card = ({ children, testId, className = "" }) => (
  <div data-testid={testId} className={`bg-[#111111] border border-white/10 p-5 ${className}`}>{children}</div>
);

const StatCard = ({ label, value, sub, unavailable, testId }) => (
  <Card testId={testId}>
    <Label>{label}</Label>
    {unavailable ? (
      <>
        <p className="mt-2 text-xl text-zinc-600 uppercase" style={anton}>Not connected</p>
        <p className="mt-1 text-[11px] text-zinc-500" style={manrope}>{unavailable}</p>
      </>
    ) : (
      <>
        <p className="mt-2 text-4xl text-white" style={anton}>{value}</p>
        {sub && <p className="mt-1 text-[11px] text-zinc-500" style={manrope}>{sub}</p>}
      </>
    )}
  </Card>
);

const BarList = ({ rows, testId }) => {
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <div className="space-y-2" data-testid={testId}>
      {rows.length === 0 && <p className="text-zinc-600 text-sm" style={manrope}>No records in this period.</p>}
      {rows.map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-xs text-zinc-400" style={manrope}><span>{k}</span><span className="text-white">{v}</span></div>
          <div className="mt-1 h-2 bg-white/5"><div className="h-2 bg-white" style={{ width: `${(v / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
};

const MiniBars = ({ series, testId }) => {
  const max = Math.max(1, ...series.map((s) => s[1]));
  return (
    <div className="flex items-end gap-[2px] h-20" data-testid={testId}>
      {series.length === 0 && <p className="text-zinc-600 text-sm" style={manrope}>No records in this period.</p>}
      {series.map(([d, v]) => (
        <div key={d} title={`${d}: ${v}`} className="flex-1 bg-white/80 min-w-[3px]" style={{ height: `${(v / max) * 100}%` }} />
      ))}
    </div>
  );
};

const DataTable = ({ headers, rows, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm" style={manrope}>
      <thead><tr className="border-b border-white/10">
        <th className="text-left py-2 pr-3 text-[10px] uppercase text-zinc-500" style={{ letterSpacing: "0.22em" }}>#</th>
        {headers.map((h) => <th key={h} className="text-left py-2 pr-4 text-[10px] uppercase text-zinc-500" style={{ letterSpacing: "0.22em" }}>{h}</th>)}
      </tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-white/5 text-zinc-300">
            <td className="py-2 pr-3 text-zinc-600">{i + 1}</td>
            {r.map((c, j) => <td key={j} className="py-2 pr-4">{String(c ?? "")}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Pending = ({ error, onRetry, testId }) => (
  <Card testId={testId}>
    {error ? (
      <>
        <p className="text-xl text-zinc-400 uppercase" style={anton}>Couldn&apos;t load data</p>
        <p className="mt-2 text-sm text-zinc-500" style={manrope}>{error}</p>
        <button onClick={onRetry} data-testid={`${testId}-retry`} className="mt-3 bg-white text-black text-xs uppercase font-bold px-4 py-2" style={{ letterSpacing: "0.1em" }}>Retry</button>
      </>
    ) : (
      <p className="text-sm text-zinc-500 animate-pulse" style={manrope} data-testid={`${testId}-loading`}>Loading&hellip;</p>
    )}
  </Card>
);

const NotConnectedPanel = ({ title, reason, extra, testId, statusLabel = "Not connected" }) => (
  <Card testId={testId}>
    <Label>{title}</Label>
    <p className="mt-3 text-2xl text-zinc-500 uppercase" style={anton}>{statusLabel}</p>
    <p className="mt-2 text-sm text-zinc-400 max-w-2xl" style={manrope}>{reason}</p>
    {extra}
  </Card>
);

export default function MarketingDashboard({ token, onBack, onLogout }) {
  const [tab, setTab] = useState("Overview");
  const [period, setPeriod] = useState("28");
  const [location, setLocation] = useState("all");
  const [excludeTest, setExcludeTest] = useState(true);
  const [summary, setSummary] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [connections, setConnections] = useState(null);
  const [google, setGoogle] = useState(null);
  const [errors, setErrors] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const fail = (key) => (e) => {
    if (e?.response?.status === 401) { onLogout(); return; }
    setErrors((p) => ({ ...p, [key]: e?.response ? `Request failed (HTTP ${e.response.status}). The reporting API responded with an error.` : "Network error — could not reach the reporting API." }));
  };

  const load = useCallback(() => {
    setErrors((p) => ({ ...p, summary: null }));
    axios.get(`${API}/marketing/summary`, { headers, params: { period, location, exclude_test: excludeTest } })
      .then((r) => setSummary(r.data)).catch(fail("summary"));
  }, [period, location, excludeTest, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStatic = useCallback(() => {
    setErrors((p) => ({ ...p, campaign: null, connections: null }));
    axios.get(`${API}/marketing/campaign`, { headers }).then((r) => setCampaign(r.data)).catch(fail("campaign"));
    axios.get(`${API}/marketing/connections`, { headers }).then((r) => setConnections(r.data)).catch(fail("connections"));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGoogle = useCallback(() => {
    setErrors((p) => ({ ...p, google: null }));
    axios.get(`${API}/marketing/google`, { headers, params: { period } })
      .then((r) => setGoogle(r.data)).catch(fail("google"));
  }, [period, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => { loadGoogle(); }, [loadGoogle]);

  const downloadExcel = async () => {
    setErrors((p) => ({ ...p, download: null }));
    try {
      const resp = await axios.get(`${API}/marketing/campaign-excel`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Guy-Mezger-Campaign.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.response?.status === 401) { onLogout(); return; }
      setErrors((p) => ({ ...p, download: "Download failed — please retry." }));
    }
  };

  const un = summary?.unavailable_metrics || {};
  const t = summary?.totals || {};
  const ga4 = google?.ga4;
  const gsc = google?.gsc;
  const ads = google?.ads;
  const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
  const pos = (v) => (v == null ? "—" : Number(v).toFixed(1));
  const money = (v) => (ads?.currency === "USD" || !ads?.currency ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : `${Number(v).toFixed(2)} ${ads.currency}`);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" style={manrope} data-testid="marketing-dashboard">
      <header className="border-b border-white/10 px-4 sm:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Label>Internal</Label>
          <h1 className="text-2xl uppercase" style={anton} data-testid="mkt-heading">Marketing Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onBack} data-testid="mkt-lead-pipeline-link" className="border border-white/15 text-zinc-300 hover:text-white text-xs uppercase font-bold px-4 py-2" style={{ letterSpacing: "0.1em" }}>Lead Pipeline</button>
          <button onClick={load} data-testid="mkt-refresh-btn" className="bg-white text-black text-xs uppercase font-bold px-4 py-2" style={{ letterSpacing: "0.1em" }}>Refresh</button>
          <button onClick={onLogout} data-testid="mkt-signout-btn" className="border border-white/15 text-zinc-300 hover:text-white text-xs uppercase font-bold px-4 py-2" style={{ letterSpacing: "0.1em" }}>Sign Out</button>
        </div>
      </header>

      <div className="px-4 sm:px-8 py-4 flex flex-wrap items-center gap-4 border-b border-white/10">
        <div className="flex flex-wrap gap-2">
          {TABS.map((x) => (
            <button key={x} onClick={() => setTab(x)} data-testid={`mkt-tab-${x.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`px-3 py-2 text-xs uppercase font-bold border ${tab === x ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:text-white"}`}
              style={{ letterSpacing: "0.08em" }}>{x}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-4 lg:ml-auto">
          {PERIODS.map(([v, l]) => (
            <button key={v} onClick={() => setPeriod(v)} data-testid={`mkt-period-${v}`}
              className={`text-xs uppercase font-bold pb-1 ${period === v ? "text-white border-b-2 border-white" : "text-zinc-500 hover:text-white"}`}
              style={{ letterSpacing: "0.1em" }}>{l}</button>
          ))}
          <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="mkt-location-filter"
            className="bg-[#0d0d0d] border border-white/15 text-xs text-zinc-300 px-2 py-2 uppercase">
            <option value="all">All locations</option>
            <option value="richardson">Richardson</option>
            <option value="north-dallas">North Dallas</option>
            <option value="either">Either / flexible</option>
          </select>
          <label className="flex items-center gap-2 text-xs text-zinc-400 uppercase" style={{ letterSpacing: "0.08em" }}>
            <input type="checkbox" checked={excludeTest} onChange={(e) => setExcludeTest(e.target.checked)} data-testid="mkt-exclude-test-toggle" />
            Exclude test leads
          </label>
        </div>
      </div>

      <main className="px-4 sm:px-8 py-6 space-y-6">
        {summary && (
          <p className="text-[11px] text-zinc-500" data-testid="mkt-data-source">
            Source: {summary.data_source} · Last updated {new Date(summary.last_updated).toLocaleString()} {summary.newest_record ? `· Newest record ${new Date(summary.newest_record).toLocaleString()}` : ""}
          </p>
        )}

        {tab === "Overview" && (summary ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard label="Leads" value={t.leads} sub={`${t.trial_requests} trial · ${t.private_inquiries} private`} testId="mkt-stat-leads" />
              <StatCard label="Newsletter Signups" value={t.newsletter_signups} sub="Counted separately from leads" testId="mkt-stat-newsletter" />
              <StatCard label="Paid Leads" value={t.paid_leads} sub="First-party attribution (gclid / cpc)" testId="mkt-stat-paid-leads" />
              <StatCard label="Ad Spend" unavailable={un.ad_spend?.reason} testId="mkt-stat-ad-spend" />
              <StatCard label="CPL" unavailable={un.cpl?.reason} testId="mkt-stat-cpl" />
              <StatCard label="Sessions" value={ga4?.available ? ga4.totals.sessions : undefined} sub="GA4 · site-wide" unavailable={ga4?.available ? null : (ga4?.reason || un.sessions?.reason)} testId="mkt-stat-sessions" />
              <StatCard label="Users" value={ga4?.available ? ga4.totals.users : undefined} sub="GA4 · site-wide" unavailable={ga4?.available ? null : (ga4?.reason || un.users?.reason)} testId="mkt-stat-users" />
              <StatCard label="SEO Clicks" value={gsc?.available ? gsc.totals.clicks : undefined} sub="Search Console · site-wide" unavailable={gsc?.available ? null : (gsc?.reason || un.seo_clicks?.reason)} testId="mkt-stat-seo-clicks" />
              <StatCard label="Impressions" value={gsc?.available ? gsc.totals.impressions : undefined} sub="Search Console · site-wide" unavailable={gsc?.available ? null : (gsc?.reason || un.seo_impressions?.reason)} testId="mkt-stat-impressions" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card testId="mkt-channels-card"><Label>First-touch channel</Label><div className="mt-3"><BarList rows={summary.channels} testId="mkt-channels-bars" /></div></Card>
              <Card testId="mkt-locations-card"><Label>Location breakdown</Label><div className="mt-3"><BarList rows={summary.by_location} testId="mkt-locations-bars" /></div></Card>
            </div>
            <Card testId="mkt-daily-card"><Label>Daily leads</Label><div className="mt-3"><MiniBars series={summary.daily_leads} testId="mkt-daily-bars" /></div></Card>
          </>
        ) : <Pending error={errors.summary} onRetry={load} testId="mkt-overview-pending" />)}

        {tab === "CRM Funnel" && (summary ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card testId="mkt-funnel-card">
              <Label>Captured → CRM email delivery (read-only, actual statuses)</Label>
              <div className="mt-3"><BarList rows={[["Captured leads", summary.crm_funnel.captured_leads],
                ...Object.entries(summary.crm_funnel.crm_email_jobs)]} testId="mkt-funnel-bars" /></div>
              <p className="mt-3 text-[11px] text-zinc-500">Email "accepted/delivered" = delivery to the club inbox only — it is NOT CRM lead/trial/sale confirmation.</p>
            </Card>
            <NotConnectedPanel title="CRM-confirmed leads / trials / sales" reason={summary.crm_funnel.crm_confirmed.reason} testId="mkt-crm-confirmed-panel" />
          </div>
        ) : <Pending error={errors.summary} onRetry={load} testId="mkt-funnel-pending" />)}

        {tab === "SEO & Search" && (campaign ? (
          <>
            {gsc?.available && (
              <p className="text-[11px] text-zinc-500" data-testid="mkt-gsc-meta">
                {gsc.source} · {gsc.date_range.start} → {gsc.date_range.end} · {gsc.timezone} · Fetched {new Date(gsc.fetched_at).toLocaleString()}{gsc.cached ? " (cached)" : ""} · {gsc.freshness_note} · {gsc.scope_note}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard label="Clicks" value={gsc?.available ? gsc.totals.clicks : undefined} sub={gsc?.totals?.note} unavailable={gsc?.available ? null : (gsc?.reason || un.seo_clicks?.reason)} testId="mkt-seo-clicks" />
              <StatCard label="Impressions" value={gsc?.available ? gsc.totals.impressions : undefined} unavailable={gsc?.available ? null : (gsc?.reason || un.seo_impressions?.reason)} testId="mkt-seo-impressions" />
              <StatCard label="CTR" value={gsc?.available ? pct(gsc.totals.ctr) : undefined} unavailable={gsc?.available ? null : (gsc?.reason || "Search Console API not connected")} testId="mkt-seo-ctr" />
              <StatCard label="Avg Position" value={gsc?.available ? pos(gsc.totals.position) : undefined} unavailable={gsc?.available ? null : (gsc?.reason || "Search Console API not connected")} testId="mkt-seo-position" />
            </div>
            {gsc?.available && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Card testId="mkt-gsc-queries-card"><Label>Top queries ({gsc.top_queries.length}) — live Search Console</Label>
                  <div className="mt-3"><DataTable headers={["Query", "Clicks", "Impr.", "CTR", "Pos."]}
                    rows={gsc.top_queries.map((q) => [q.query, q.clicks, q.impressions, pct(q.ctr), pos(q.position)])} testId="mkt-gsc-queries-table" /></div></Card>
                <Card testId="mkt-gsc-pages-card"><Label>Top pages ({gsc.top_pages.length}) — live Search Console</Label>
                  <div className="mt-3"><DataTable headers={["Page", "Clicks", "Impr.", "CTR", "Pos."]}
                    rows={gsc.top_pages.map((q) => [q.page, q.clicks, q.impressions, pct(q.ctr), pos(q.position)])} testId="mkt-gsc-pages-table" /></div></Card>
              </div>
            )}
            <Card testId="mkt-seo-baseline-card">
              <Label>Historical organic baseline — {campaign.baseline.period}</Label>
              <p className="mt-1 text-[11px] text-zinc-500">{campaign.baseline.source}</p>
              <div className="mt-3"><DataTable headers={["Query", "Clicks", "Impressions", "CTR", "Position", "Source"]}
                rows={campaign.baseline.rows.map((r) => [r.query, r.clicks, r.impressions, typeof r.ctr === "number" ? `${(r.ctr * 100).toFixed(1)}%` : r.ctr, r.position, r.source])} testId="mkt-baseline-table" /></div>
              <p className="mt-2 text-[11px] text-zinc-500">{campaign.baseline.note}</p>
            </Card>
          </>
        ) : <Pending error={errors.campaign} onRetry={loadStatic} testId="mkt-seo-pending" />)}

        {tab === "Analytics" && (ga4?.available ? (
          <>
            <p className="text-[11px] text-zinc-500" data-testid="mkt-ga4-meta">
              {ga4.source} · {ga4.date_range.start} → {ga4.date_range.end} · {ga4.timezone} · Fetched {new Date(ga4.fetched_at).toLocaleString()}{ga4.cached ? " (cached)" : ""} · {ga4.scope_note}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard label="Sessions" value={ga4.totals.sessions} testId="mkt-ga4-sessions" />
              <StatCard label="Users" value={ga4.totals.users} testId="mkt-ga4-users" />
              <StatCard label="New Users" value={ga4.totals.new_users} testId="mkt-ga4-new-users" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card testId="mkt-ga4-daily-card"><Label>Daily sessions</Label><div className="mt-3"><MiniBars series={ga4.daily_sessions} testId="mkt-ga4-daily-bars" /></div></Card>
              <Card testId="mkt-ga4-channels-card"><Label>Sessions by channel</Label><div className="mt-3"><BarList rows={ga4.channels} testId="mkt-ga4-channels-bars" /></div></Card>
            </div>
            <Card testId="mkt-ga4-pages-card"><Label>Top landing pages ({ga4.top_pages.length})</Label>
              <div className="mt-3"><DataTable headers={["Landing page", "Sessions", "Users"]}
                rows={ga4.top_pages.map((p) => [p.page, p.sessions, p.users])} testId="mkt-ga4-pages-table" /></div></Card>
          </>
        ) : (
          <NotConnectedPanel title="Website Analytics — GA4 property 553732498" testId="mkt-analytics-panel"
            statusLabel={ga4?.status === "pending_api_enable" ? "Pending API enable" : "Not connected"}
            reason={ga4?.reason || errors.google || "GA4 Data API reporting is not connected. Sessions, users, new users, channels, daily sessions and landing pages will appear here once a service account with Viewer access is added. The existing on-site measurement snippet (G-B98QNHDT6J) is unchanged and keeps collecting."} />
        ))}

        {tab === "Ads & ROAS" && (campaign ? (
          <>
            {ads?.available ? (
              <>
                <p className="text-[11px] text-zinc-500" data-testid="mkt-ads-meta">
                  {ads.source} · {ads.date_range.start} → {ads.date_range.end} · {ads.timezone} · {ads.currency} · Fetched {new Date(ads.fetched_at).toLocaleString()}{ads.cached ? " (cached)" : ""} · {ads.scope_note}
                </p>
                {ads.zero_activity_note && <p className="text-sm text-zinc-300" style={manrope} data-testid="mkt-ads-zero-note">{ads.zero_activity_note}</p>}
                <Label>Account totals — entire shared Hidden Gym account</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-7 gap-4" data-testid="mkt-ads-account-totals">
                  <StatCard label="Spend" value={money(ads.account_totals.cost)} testId="mkt-ads-spend" />
                  <StatCard label="Impr." value={ads.account_totals.impressions} testId="mkt-ads-impr" />
                  <StatCard label="Clicks" value={ads.account_totals.clicks} testId="mkt-ads-clicks" />
                  <StatCard label="CTR" value={pct(ads.account_totals.ctr)} testId="mkt-ads-ctr" />
                  <StatCard label="CPC" value={money(ads.account_totals.avg_cpc)} testId="mkt-ads-cpc" />
                  <StatCard label="Conversions" value={ads.account_totals.conversions} testId="mkt-ads-conversions" />
                  <StatCard label="Conv. value" value={money(ads.account_totals.conversions_value)} sub={ads.value_note} testId="mkt-ads-convvalue" />
                </div>
                <Label>Mezger / MMA totals — excludes HYROX (other brand in shared account)</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="mkt-ads-mezger-totals">
                  <StatCard label="Spend" value={money(ads.mezger_totals.cost)} testId="mkt-ads-mz-spend" />
                  <StatCard label="Clicks" value={ads.mezger_totals.clicks} testId="mkt-ads-mz-clicks" />
                  <StatCard label="Conversions" value={ads.mezger_totals.conversions} testId="mkt-ads-mz-conversions" />
                  <StatCard label="ROAS" unavailable="Awaiting ABC/Sweti checkout connection — never computed from Google Ads conversion values" testId="mkt-ads-roas" />
                </div>
                <Card testId="mkt-ads-campaigns-card"><Label>Campaigns returned by the API ({ads.campaigns.length})</Label>
                  <div className="mt-3"><DataTable headers={["Campaign", "Status", "Scope", "Impr.", "Clicks", "Spend", "Conv.", "Conv. value", "CTR", "CPC"]}
                    rows={ads.campaigns.map((c) => [c.name, c.status, c.mezger_scope ? "Mezger/MMA" : "Other brand", c.impressions, c.clicks, money(c.cost), c.conversions, money(c.conversions_value), pct(c.ctr), money(c.avg_cpc)])} testId="mkt-ads-campaigns-table" /></div>
                  <p className="mt-2 text-[11px] text-zinc-500">{ads.value_note}</p></Card>
                <Card testId="mkt-draft-campaign-card">
                  <Label>Prepared draft — {ads.draft_campaign.name} (ID {ads.draft_campaign.id})</Label>
                  <p className="mt-2 text-sm text-zinc-400 max-w-3xl">{ads.draft_campaign.status}. {campaign.snapshot_note}</p>
                  <div className="mt-3"><DataTable headers={["Setting", "Configured value"]} rows={campaign.settings.map((s) => [s.setting, s.value])} testId="mkt-campaign-settings-table" /></div>
                </Card>
              </>
            ) : (
              <>
                <NotConnectedPanel title="Google Ads — customer 714-925-9790" testId="mkt-ads-unavailable-panel"
                  statusLabel={ads?.status === "pending_api_enable" ? "Pending API enable" : ads?.status === "permission_denied" ? "Permission denied" : "Not connected"}
                  reason={ads?.reason || errors.google || "Google Ads reporting data has not loaded. Failed requests are never shown as zeros."} />
                <Card testId="mkt-draft-campaign-card">
                  <Label>{campaign.snapshot_label}</Label>
                  <p className="mt-2 text-sm text-zinc-400 max-w-3xl">{campaign.snapshot_note}</p>
                  <div className="mt-3"><DataTable headers={["Setting", "Configured value"]} rows={campaign.settings.map((s) => [s.setting, s.value])} testId="mkt-campaign-settings-table" /></div>
                </Card>
              </>
            )}
          </>
        ) : <Pending error={errors.campaign} onRetry={loadStatic} testId="mkt-ads-pending" />)}

        {tab === "GBP & Revenue" && (
          <>
            <NotConnectedPanel title="Google Business Profile — Mezger Martial Arts at Hidden Gym (North Dallas)" testId="mkt-gbp-panel"
              statusLabel="Pending Google approval"
              reason="The GBP Performance API is enabled on the Cloud project, but its quota is currently 0 — Google must approve a quota request before any data can be read. Once approved, this panel will show profile views, call clicks, website clicks and direction requests for the verified Mezger listing only."
              extra={
                <details className="mt-3 text-[12px] text-zinc-400" data-testid="mkt-gbp-tech-details">
                  <summary className="cursor-pointer text-zinc-500 uppercase text-[11px]" style={{ letterSpacing: "0.1em" }}>Technical setup details</summary>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>Listing: locations/7311582123311843071 (Mezger Martial Arts at Hidden Gym North Dallas)</li>
                    <li>Hidden Gym's own listings are separate and are never aggregated into Mezger reporting</li>
                    <li>Business Profile Performance API enabled on project inner-encoder-487019-k1; default quota 0 — Google approval form required</li>
                    <li>No GBP credentials stored yet — nothing to misreport; failed or missing access is never shown as zeros</li>
                  </ul>
                </details>
              } />
            <NotConnectedPanel title="Revenue — ABC/Sweti hosted checkout" testId="mkt-revenue-panel"
              statusLabel="Awaiting ABC/Sweti checkout connection"
              reason="Membership revenue lives in the ABC/Sweti hosted checkout subdomain. Until that connection is verified, no dollar figures are shown — revenue is never inferred from leads, joins, or Google Ads conversion values."
              extra={
                <details className="mt-3 text-[12px] text-zinc-400" data-testid="mkt-revenue-tech-details">
                  <summary className="cursor-pointer text-zinc-500 uppercase text-[11px]" style={{ letterSpacing: "0.1em" }}>Technical setup details</summary>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>Requested from Greg (Oct 13, 2026): cross-domain tags on the checkout subdomain</li>
                    <li>Preserved UTMs and GCLID through the checkout handoff</li>
                    <li>A successful-membership event carrying brand, location and a unique transaction ID</li>
                    <li>This site's membership CTAs are internal lead pages — no checkout URLs or public tags were changed</li>
                  </ul>
                </details>
              } />
          </>
        )}

        {tab === "Campaign & Ad Copy" && (campaign ? (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div><Label>{campaign.snapshot_label}</Label><p className="mt-1 text-[11px] text-zinc-500 max-w-2xl">{campaign.snapshot_note}</p></div>
              <button onClick={downloadExcel} data-testid="mkt-download-excel-btn" className="bg-white text-black text-xs uppercase font-bold px-5 py-3" style={{ letterSpacing: "0.1em" }}>Download Campaign Excel</button>
              {errors.download && <p className="w-full text-xs text-red-400" style={manrope} data-testid="mkt-download-error">{errors.download}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="mkt-campaign-keyfacts">
              {[["Daily Budget", "$30/day"], ["Max CPC", "$3"],
                ["Targeting", "5-mile radius · North Dallas · Presence only"],
                ["Landing Page", campaign.settings.find((s) => s.setting === "Landing page")?.value || ""]].map(([l, v]) => (
                <Card key={l} testId={`mkt-keyfact-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <Label>{l}</Label>
                  <p className="mt-2 text-lg text-white break-all" style={anton}>{v}</p>
                </Card>
              ))}
            </div>
            <Card testId="mkt-headlines-card"><Label>Ad copy — {campaign.headlines.length} headlines · {campaign.descriptions.length} descriptions</Label>
              <div className="mt-3"><DataTable headers={["Asset", "Copy", "Chars", "Limit"]}
                rows={[...campaign.headlines, ...campaign.descriptions].map((h) => [h.asset, h.copy, h.characters, h.limit])} testId="mkt-adcopy-table" /></div></Card>
            <Card testId="mkt-sitelinks-card"><Label>Sitelinks ({campaign.sitelinks.length})</Label>
              <div className="mt-3"><DataTable headers={["Sitelink", "Details", "Status"]} rows={campaign.sitelinks.map((s) => [s.sitelink, s.details, s.status])} testId="mkt-sitelinks-table" /></div></Card>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <Card testId="mkt-keywords-card"><Label>Target keywords ({campaign.keywords.length}) — saved in draft</Label>
                <div className="mt-3"><DataTable headers={["Keyword", "Match", "Ad group", "Status"]} rows={campaign.keywords.map((k) => [k.keyword, k.match_type, k.ad_group, k.status])} testId="mkt-keywords-table" /></div></Card>
              <Card testId="mkt-negatives-card"><Label>Negative keywords ({campaign.negatives.length}) — prepared, NOT applied</Label>
                <div className="mt-3"><DataTable headers={["Negative", "Match", "Scope", "Status"]} rows={campaign.negatives.map((k) => [k.keyword, k.match_type, k.scope, k.status])} testId="mkt-negatives-table" /></div></Card>
            </div>
          </>
        ) : <Pending error={errors.campaign} onRetry={loadStatic} testId="mkt-campaign-pending" />)}

        {tab === "Connections" && (connections ? (
          <>
            {connections.connections.map((c) => {
              const live = c.id === "ga4" ? ga4 : c.id === "gsc" ? gsc : c.id === "google_ads" ? ads : null;
              const status = live?.status || c.status;
              return (
              <Card key={c.id} testId={`mkt-connection-${c.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div><Label>{c.name}</Label><p className="mt-1 text-sm text-zinc-300">{c.target}</p></div>
                  <span data-testid={`mkt-connection-${c.id}-status`} className={`text-xs uppercase font-bold px-3 py-1 border ${status === "connected" ? "border-white text-white" : status === "pending_api_enable" ? "border-white/40 text-zinc-300" : "border-white/15 text-zinc-500"}`} style={{ letterSpacing: "0.1em" }}>{status.replace(/_/g, " ")}</span>
                </div>
                {live && !live.available && live.reason && <p className="mt-2 text-[12px] text-zinc-400">{live.reason}</p>}
                <ul className="mt-3 space-y-1 text-[12px] text-zinc-400 list-disc list-inside">
                  {c.prerequisites.map((p) => <li key={p}>{p}</li>)}
                </ul>
              </Card>
              );
            })}
            <p className="text-[11px] text-zinc-500">{connections.note}</p>
          </>
        ) : <Pending error={errors.connections} onRetry={loadStatic} testId="mkt-connections-pending" />)}
      </main>
    </div>
  );
}
